import { FocusEvent, forwardRef, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'use-debounce';
import { BaseSelectProps } from '~/components/inputs/Select/Select';
import { useAllBookkeepingAccountsQuery } from '~/services/bookkeeping/bookkeeping-accounts';
import { BookkeepingAccount, BookkeepingAccountFilters } from '~/types/general-bookkeeping';
import { bookkeepingAccountLabel, checkBookkeepingAccountMatch } from '~/utils/bookkeeping/bookkeeping-account';
import { sortObjectsAlphabetically } from '~/utils/sort';
import SimpleComboBox, { SimpleComboboxOption } from './SimpleComboBox';

export interface BookkeepingAccountSelectProps extends BaseSelectProps {
  onChange: (value: string | null, account: BookkeepingAccount | null) => void;
  value: string | null;
  placeholder?: string;
  clearable?: boolean;
  filters?: BookkeepingAccountFilters;
}

function accountToOption(account: BookkeepingAccount): SimpleComboboxOption {
  return {
    label: bookkeepingAccountLabel(account),
    value: account.id,
    description: `${account.section.charAt(0).toUpperCase() + account.section.slice(1)} (${account.accountType})`,
  };
}

const BookkeepingAccountSelect = forwardRef<HTMLDivElement, BookkeepingAccountSelectProps>(
  ({ onChange, value, placeholder, clearable = true, filters = { hasChildren: false }, ...props }, ref) => {
    const { data: accounts } = useAllBookkeepingAccountsQuery(filters);
    const { t } = useTranslation('inputs', { keyPrefix: 'bookkeeping_account_select' });
    const [searchText, setSearchText] = useState<string | null>(null);
    const [debouncedSearchText] = useDebounce(searchText ?? '', 150);

    const selectedLabel = useMemo(() => {
      if (!value) return '';
      const account = (accounts ?? []).find((a) => a.id === value);
      return account ? bookkeepingAccountLabel(account) : '';
    }, [accounts, value]);

    const options = useMemo(() => {
      const accountOptions: SimpleComboboxOption[] = (accounts ?? [])
        .filter((account) => checkBookkeepingAccountMatch(account, debouncedSearchText))
        .slice(0, 200)
        .map(accountToOption)
        .sort(sortObjectsAlphabetically((item) => item.label));

      return accountOptions;
    }, [accounts, debouncedSearchText]);

    const handleChange = useCallback(
      (selectedValue: string | null) => {
        const selectedAccount = (accounts ?? []).find((account) => account.id === selectedValue) ?? null;
        setSearchText(null);
        onChange(selectedValue, selectedAccount);
      },
      [accounts, onChange],
    );

    const handleBlur = useCallback(
      (event: FocusEvent<Element, Element>) => {
        setSearchText(null);
        props.onBlur?.(event);
      },
      [props],
    );

    return (
      <SimpleComboBox
        {...props}
        ref={ref}
        onChange={handleChange}
        options={options}
        inputValue={searchText ?? selectedLabel}
        value={value}
        className={props.className}
        placeholder={placeholder || t('select_account')}
        hasClearButton={clearable}
        onInputChange={setSearchText}
        onBlur={handleBlur}
      />
    );
  },
);

BookkeepingAccountSelect.displayName = 'BookkeepingAccountSelect';

export default BookkeepingAccountSelect;
